import { BehaviorSubject, combineLatest, of, switchMap, take } from 'rxjs';
import { DevToolsRegistry } from '../../dev-tools';
import { ComponentClass, DynamicComponentHelper, ObservableComponentInfo, ObservableComponentsInfo } from '../../dynamic-components';
import { FeatureService } from '../feature-registry';
import { getReplaceableComponentId } from './replaceable-component.decorator';
import { ReplaceableComponentsRegistry } from './replaceable-components.registry';
import { takeWhileAlive } from '../../utils';
import * as i0 from "@angular/core";
import * as i1 from "../feature-registry";
import * as i2 from "../../dev-tools";
export let FeatureDynamicComponentsService = /*#__PURE__*/(() => {
  class FeatureDynamicComponentsService {
    constructor(features, debug) {
      this.features = features;
      this.replacementComponents$ = new BehaviorSubject(new Map());
      this.widgetComponents$ = new BehaviorSubject(new Map());
      this.features.getLoadedFeatures().pipe(takeWhileAlive()).subscribe(activatedFeatures => {
        const replacementManifestsMap = new Map();
        const widgetManifests = [];
        activatedFeatures.forEach(feature => {
          const definition = feature.getFeatureFunctionalityCached();
          if (definition.dynamicComponents) {
            for (const manifest of definition.dynamicComponents) {
              if (manifest.replaceComponent) {
                if (replacementManifestsMap.has(manifest.replaceComponent)) throw new Error('Error: A feature (' + definition.name + ') is trying to add a replacement' + ' for a component already being replaced (' + manifest.replaceComponent + ') ');
                replacementManifestsMap.set(manifest.replaceComponent, manifest);
              } else if (manifest.widgets) {
                widgetManifests.push(manifest);
              }
            }
          }
        });
        this.replacementComponents$.next(replacementManifestsMap);
        const manifestsByWidgetId = widgetManifests.reduce((groups, manifest) => {
          for (const widgetId of manifest.widgets) {
            const group = groups.get(widgetId) || [];
            group.push(manifest);
            groups.set(widgetId, group);
          }
          return groups;
        }, new Map());
        this.widgetComponents$.next(manifestsByWidgetId);
      });
      debug.exposeDebugFunction(['Features', 'Inspect Cmp Replacements'], () => {
        return this.replacementComponents$.pipe(take(1));
      });
      debug.exposeDebugFunction(['Features', 'Inspect Registered Widgets'], () => {
        return this.widgetComponents$.pipe(take(1));
      });
    }
    /**
     * used by CustomRouter to check if a routed component should be replaced.
     * discovers component replacements provided by feature plugins thru FrontendFeatureFunctionality.componentReplacements
     */
    resolveComponentReplacement(info) {
      let id;
      if (typeof info !== 'string') {
        // resolve component replacement ID
        id = getReplaceableComponentId(info);
      } else {
        id = info;
      }
      // console.log('info/id',info, id)
      if (id === null) return of(null);
      return this.replacementComponents$.pipe(switchMap(manifests => {
        return this.loadComponentFromManifest(manifests.get(id) || null);
      }));
    }
    /**
     * used by ReplaceableComponent directive to check if a replacement and/or a default component exists
     */
    resolveComponent(id, throwIfMissingDefault = false) {
      const DefaultComponent = ReplaceableComponentsRegistry.get(id) || null;
      if (!DefaultComponent && throwIfMissingDefault) throw new Error('No (default) component with replacementId "' + id + '" has been registered!');
      // console.log('info/id',info, id)
      if (id === null) return of(null);
      return this.replacementComponents$.pipe(switchMap(manifests => {
        const replacement = manifests.get(id) || null;
        if (!replacement) return of({
          component: DefaultComponent,
          module: null
        });
        return this.loadComponentFromManifest(replacement);
      }));
    }
    resolveWidgetComponents(id) {
      return this.widgetComponents$.pipe(switchMap(manifestGroups => {
        const manifestGroup = manifestGroups.get(id) || [];
        const sources = manifestGroup.map(manifest => this.loadComponentFromManifest(manifest));
        return combineLatest(sources);
      }));
    }
    loadComponentFromManifest(manifest) {
      if (manifest === null) return of(null);
      return DynamicComponentHelper.loadComponent(manifest.component || manifest.load || null);
    }
    /**
     * getX methods will return manifest(s) if found
     */
    getReplacementForComponent(id) {
      return this.replacementComponents$.getValue().get(id) || null;
    }
    getComponentsForWidget(id) {
      return this.widgetComponents$.getValue().get(id) || [];
    }
    static {
      this.ɵfac = function FeatureDynamicComponentsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeatureDynamicComponentsService)(i0.ɵɵinject(i1.FeatureService), i0.ɵɵinject(i2.DevToolsRegistry));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: FeatureDynamicComponentsService,
        factory: FeatureDynamicComponentsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FeatureDynamicComponentsService;
})();